import { Table, Typography, Spin } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { renderDpatEvidenceLink } from "utils/renderDpatEvidenceLink";
import {
  formatTrackerDate,
  getAttributeValue,
  getFileLinkIfExist,
  getTrackerEventDataValue,
  getYearFromDateValue,
} from "utils/utils";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { isDpatIncidentYear, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";

const { Title } = Typography;

const PROGRAM_ID = "pYvmkB4s6Nq";
const UNIT_ACTIVITIES_REPORT_STAGE = "Idy1ecQekdu";
const REPORT_FILE_DATA_ELEMENT = "hM6AUNKRbKB";
const STAFF_LIST_DATA_ELEMENT = "efetkc9u7Wx";
const STAFF_CATEGORY_ATTRIBUTE = "RXTTz95xaoO";
const OFFICE_AVAILABLE_ATTRIBUTE = "w9leRrwWrLB";
const ESTABLISHED_DATE_ATTRIBUTE = "AlVMRjjWuVo";
const REPORT_TITLE_DATA_ELEMENT = "LoiGIIgSRDs";
const ISSUES_REPORTED_DATA_ELEMENT = "lVsNRzeoVLB";

function resolveYearValue(year) {
  if (year == null || year === "") return null;
  if (typeof year === "object") {
    const nested = year.value ?? year.year ?? year.id;
    const nestedNum = Number(nested);
    return Number.isFinite(nestedNum) ? nestedNum : null;
  }
  const yearNum = Number(year);
  return Number.isFinite(yearNum) ? yearNum : null;
}

/** Prefer calendar year from occurredAt string (YYYY-...) to avoid timezone shifts. */
function getOccurredAtYear(occurredAt) {
  if (occurredAt == null || occurredAt === "") return null;
  const match = String(occurredAt).match(/^(\d{4})/);
  if (match) return Number(match[1]);
  return getYearFromDateValue(occurredAt);
}

function isOccurredAtInSelectedYear(occurredAt, selectedYear) {
  const yearNum = resolveYearValue(selectedYear);
  const occurredYear = getOccurredAtYear(occurredAt);
  if (yearNum == null || occurredYear == null) return false;
  if (isDpatScoreRelaxationExempt()) {
    return isDpatIncidentYear(occurredYear, yearNum);
  }
  return occurredYear === yearNum;
}

function getEventProgramStageId(event) {
  const stage = event?.programStage;
  if (!stage) return "";
  if (typeof stage === "string") return stage;
  return stage.id || stage.value || "";
}

function getInspectorateUnitAttribute(unit, displayNames, attributeId) {
  for (const name of displayNames) {
    const value = getAttributeValue(name, unit);
    if (value && value !== "N/A") {
      return value;
    }
  }

  const matched = unit?.attributes?.find((attr) => attr.attribute === attributeId);
  return matched?.value || "N/A";
}

function toEvidenceUrl(eventUid, dataElementUid) {
  return eventUid
    ? `https://dddp.gov.gh/api/events/files?eventUid=${eventUid}&dataElementUid=${dataElementUid}`
    : "Not Uploaded";
}

function buildUnitsPayload(instances, events, selectedYear) {
  const unitRows = (instances || []).map((unit, index) => {
    const officeValue = getInspectorateUnitAttribute(
      unit,
      [
        "Has the office been made available for the unit",
        "Office Available (Yes/No)",
      ],
      OFFICE_AVAILABLE_ATTRIBUTE,
    );
    const officeAvailable = ["true", "yes", "YES", true].includes(officeValue);
    const reportEventUid = getFileLinkIfExist(
      events,
      REPORT_FILE_DATA_ELEMENT,
      unit.trackedEntity,
    );
    const staffListEventUid = getFileLinkIfExist(
      events,
      STAFF_LIST_DATA_ELEMENT,
      unit.trackedEntity,
    );

    return {
      key: unit.trackedEntity || index + 1,
      date: getInspectorateUnitAttribute(unit, ["Established Date"], ESTABLISHED_DATE_ATTRIBUTE),
      officeAvailability: officeAvailable ? "YES" : "NO",
      staffCategory: getInspectorateUnitAttribute(
        unit,
        [
          "Category of Staff",
          "DPAT | Category of Staff",
          "List of Staff",
          "DPAT | List of Staff",
        ],
        STAFF_CATEGORY_ATTRIBUTE,
      ),
      report: toEvidenceUrl(reportEventUid, REPORT_FILE_DATA_ELEMENT),
      staffList: toEvidenceUrl(staffListEventUid, STAFF_LIST_DATA_ELEMENT),
    };
  });

  const reportRows = (events || [])
    .filter((event) => {
      const stageId = getEventProgramStageId(event);
      return !stageId || stageId === UNIT_ACTIVITIES_REPORT_STAGE;
    })
    .filter((event) => isOccurredAtInSelectedYear(event.occurredAt, selectedYear))
    .map((event, index) => {
      const title = getTrackerEventDataValue(
        event,
        REPORT_TITLE_DATA_ELEMENT,
        ["Report Title", "Title", "Type"],
      );
      const issues = getTrackerEventDataValue(
        event,
        ISSUES_REPORTED_DATA_ELEMENT,
        ["Issues Reported on", "Identified issue", "Identified Issue"],
      );

      return {
        key: event.event || index + 1,
        title: title !== "N/A" ? title : "",
        date: formatTrackerDate(event.occurredAt),
        issues: issues !== "N/A" ? issues : "",
        occurredAt: event.occurredAt,
      };
    })
    .sort((a, b) => new Date(a.occurredAt || a.date) - new Date(b.occurredAt || b.date));

  return {
    data: unitRows,
    report: reportRows,
  };
}

const BuildingInspectorateUnit = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [units, setUnits] = useState({ data: [], report: [] });
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [maxScore] = useState(3);
  const [isLoading, setIsLoading] = useState(false);

  const resolved = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return null;
    return {
      units: savedAssessmentData.units ?? units,
      scorei: savedAssessmentData.scorei ?? 0,
      scoreii: savedAssessmentData.scoreii ?? 0,
      maxScore: savedAssessmentData.maxScore ?? 3,
    };
  }, [isAssessmentStarted, savedAssessmentData, units]);

  const displayUnits = resolved?.units ?? units;
  const displayUnitRows = displayUnits?.data || [];
  // Already filtered by occurredAt year during fetch.
  const displayReports = displayUnits?.report || [];
  const displayScorei = resolved?.scorei ?? scorei;
  const displayScoreii = resolved?.scoreii ?? scoreii;
  const displayMaxScore = resolved?.maxScore ?? maxScore;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI3",
      area: "Physical and Spatial Planning Services",
      maxScore: displayMaxScore,
      units: {
        ...(displayUnits || {}),
        data: displayUnitRows,
        report: displayReports,
      },
      scorei: displayScorei,
      scoreii: displayScoreii,
    }),
  }), [displayUnits, displayUnitRows, displayReports, displayMaxScore, displayScorei, displayScoreii]);

  const loadFromDhis2 = useCallback(() => {
    const selectedYear = resolveYearValue(year);
    if (!districtId || selectedYear == null) {
      setUnits({ data: [], report: [] });
      setScorei(0);
      setScoreii(0);
      return;
    }

    setIsLoading(true);

    axios
      .get(`/tracker/trackedEntities?orgUnit=${districtId}&program=${PROGRAM_ID}&pageSize=5000`)
      .then((result) => {
        const instances = result.data?.instances || [];

        return axios
          .get(
            `/tracker/events?program=${PROGRAM_ID}&orgUnit=${districtId}&pageSize=5000`
            + `&fields=event,trackedEntity,programStage,createdAt,scheduledAt,occurredAt,eventDate,dataValues[dataElement,displayName,value]`,
          )
          .then((resp) => {
            const events = resp.data?.instances || resp.data?.events || [];
            const payload = buildUnitsPayload(instances, events, selectedYear);
            setUnits(payload);
            setScorei(payload.data.length > 0 ? 1 : 0);
            setScoreii(payload.report.length > 0 ? 2 : 0);
          });
      })
      .catch((err) => {
        console.error(err);
        setUnits({ data: [], report: [] });
        setScorei(0);
        setScoreii(0);
      })
      .finally(() => setIsLoading(false));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.units) setUnits(snap.units);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
    },
    deps: [districtId, year, loadFromDhis2],
  });

  const DPAT_SLUG = "sdi3.0-3.1";

  const buildingInspectorateUnitColumn = [
    { title: "Date Established", dataIndex: "date", key: "date" },
    { title: "Office Available (Yes/No)", dataIndex: "officeAvailability", key: "officeAvailability" },
    { title: "Category of Staff", dataIndex: "staffCategory", key: "staffCategory" },
    {
      title: "Report",
      dataIndex: "report",
      key: "report",
      render: (value) => renderDpatEvidenceLink(value, "View Report"),
    },
    {
      title: "List of Staff",
      dataIndex: "staffList",
      key: "staffList",
      render: (value) => renderDpatEvidenceLink(value, "View List"),
    },
  ];

  const buildingInspectorateUnitReportColumn = [
    { title: "Report Title", dataIndex: "title", key: "title" },
    { title: "Date of Report", dataIndex: "date", key: "date" },
    { title: "Issues Reported on", dataIndex: "issues", key: "issues" },
  ];

  return (
    <Spin spinning={Boolean(assessmentStartLoading || isLoading)} tip="Loading Building Inspectorate Unit data...">
      <Comment
        data={displayUnitRows}
        year={year}
        districtId={districtId}
        tableCommentedId={`sdi3.0-3.1-${year}`}
        hideComment={hideComment}
      >
        {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
            <DpatIndicatorCmsIntro
              year={year}
              slug={DPAT_SLUG}
              mainTitleStyle={{ marginTop: "30px" }}
            />

            <IndicatorScoreSection>
              <MaxScoreDisplay score={displayMaxScore} />
              <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 3.0-3.1i Actual Score:" score={displayScorei} />
              <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 3.0-3.1ii Actual Score:" score={displayScoreii} />
            </IndicatorScoreSection>

            <Title level={4} style={{ marginTop: "20px" }}>
              Evidence of establishment of Planning & Building Inspectorate Unit
            </Title>
            <Table
              columns={buildingInspectorateUnitColumn}
              dataSource={displayUnitRows}
              rowKey="key"
              pagination={false}
              bordered
              locale={{ emptyText: "No establishment data available" }}
            />

            <Title level={4} style={{ marginTop: "20px" }}>
              Evidence of Reports on activities of the Unit
            </Title>
            <Table
              columns={buildingInspectorateUnitReportColumn}
              dataSource={displayReports}
              rowKey="key"
              pagination={false}
              bordered
              locale={{ emptyText: "No reports available for the selected year" }}
            />

            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
        )}
      </Comment>
    </Spin>
  );
});

export default BuildingInspectorateUnit;
